import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/noir/Navbar";
import Footer from "@/components/noir/Footer";
import StickyMobileCTA from "@/components/noir/StickyMobileCTA";
import { api, CONTACT } from "@/lib/api";
import { ArrowRight, MapPin, Phone, Instagram, MessageCircle, Navigation } from "lucide-react";

const HERO_IMG = "https://images.pexels.com/photos/13068379/pexels-photo-13068379.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1400&w=1800";
const STORY_IMG = "https://images.pexels.com/photos/4677845/pexels-photo-4677845.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1000&w=800";
const IG_IMGS = [
  "https://images.unsplash.com/photo-1587729927031-830c32f520da?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  "https://images.pexels.com/photos/6830805/pexels-photo-6830805.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=800&w=800",
  "https://images.pexels.com/photos/13277178/pexels-photo-13277178.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=800&w=800",
  "https://images.unsplash.com/photo-1632345031435-8727f6897d53?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  "https://images.unsplash.com/photo-1604654894610-df63bc536371?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
];

function Reveal({ children, className = "" }) {
  const [visible, setVisible] = useState(false);
  return (
    <div
      ref={(el) => {
        if (!el || visible) return;
        const io = new IntersectionObserver(([e]) => e.isIntersecting && (setVisible(true), io.disconnect()), { threshold: 0.15 });
        io.observe(el);
      }}
      className={`reveal ${visible ? "visible" : ""} ${className}`}
    >
      {children}
    </div>
  );
}

export default function Home() {
  const [categories, setCategories] = useState([]);
  const [selected, setSelected] = useState(new Set());

  useEffect(() => {
    api.get("/services").then((r) => setCategories(r.data.categories || []));
  }, []);

  const toggle = (name) => {
    const s = new Set(selected);
    s.has(name) ? s.delete(name) : s.add(name);
    setSelected(s);
  };

  const bookingHref = selected.size
    ? `/book?services=${encodeURIComponent(Array.from(selected).join("|"))}`
    : "/book";

  return (
    <div id="top" className="bg-[#0B0B0B] text-[#F7F3EC] min-h-screen">
      <Navbar />

      {/* HERO */}
      <section className="relative min-h-[90vh] md:min-h-screen w-full overflow-hidden grain">
        <img src={HERO_IMG} alt="NOIR NAILS luxury nail studio interior in Whitefield" className="absolute inset-0 w-full h-full object-cover opacity-55" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B0B0B]/60 via-[#0B0B0B]/40 to-[#0B0B0B]" />
        <div className="relative z-10 max-w-6xl mx-auto px-6 md:px-10 pt-40 md:pt-48 pb-24">
          <p className="eyebrow" data-testid="hero-eyebrow">Whitefield · Bangalore</p>
          <h1 className="serif text-[3.2rem] leading-[0.95] md:text-[6.5rem] lg:text-[8rem] mt-6 tracking-tight text-[#F7F3EC]" data-testid="hero-title">
            NOIR<br />NAILS
          </h1>
          <div className="gold-line mt-8 max-w-[240px]" />
          <p className="serif-italic mt-8 text-2xl md:text-3xl text-[#F7F3EC]/90" data-testid="hero-tagline">
            Your style. Your statement. Your NOIR.
          </p>
          <p className="mt-8 max-w-xl text-base md:text-lg text-[#A1A1AA] leading-relaxed">
            Where beauty meets detail, creativity meets confidence, and every set is uniquely yours.
          </p>
          <div className="mt-12 flex flex-col sm:flex-row gap-4">
            <Link to="/book" data-testid="hero-book-btn" className="btn-primary group">
              Book an Appointment <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
            </Link>
            <a href="#services" data-testid="hero-services-btn" className="btn-outline">
              Explore Our Services
            </a>
          </div>
        </div>
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-xs tracking-[0.4em] uppercase text-[#A1A1AA]">Scroll</div>
      </section>

      {/* OUR STORY */}
      <section id="story" className="relative py-24 md:py-40 px-6 md:px-12">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-16 items-center">
          <Reveal>
            <div className="relative">
              <img src={STORY_IMG} alt="Nail artist crafting a set at NOIR NAILS" className="w-full h-[520px] object-cover" />
              <div className="absolute -bottom-6 -right-6 hidden md:block">
                <div className="border border-[#B8935A] px-6 py-4">
                  <p className="eyebrow">Est. 2026</p>
                </div>
              </div>
            </div>
          </Reveal>
          <Reveal>
            <p className="eyebrow">Our Story</p>
            <h2 className="serif text-4xl md:text-6xl mt-4 leading-tight">
              Nails are more than beauty — <span className="serif-italic text-[#B8935A]">they are you.</span>
            </h2>
            <div className="gold-line my-8 max-w-[120px]" />
            <div className="space-y-6 text-[#A1A1AA] leading-relaxed text-base md:text-lg">
              <p>
                NOIR NAILS was created for those who believe nails are more than just beauty — they're a reflection of your style.
              </p>
              <p>
                Our mission is simple: to create beautiful, detailed nail experiences in a space where you can feel confident, comfortable, and truly yourself.
              </p>
              <p>
                From timeless elegance to bold, expressive designs, every set at NOIR NAILS is crafted with care, creativity, and attention to detail.
              </p>
              <p className="serif-italic text-xl md:text-2xl text-[#F7F3EC] pt-4">
                Your style. Your statement. Your NOIR.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* SERVICES */}
      <section id="services" className="relative py-24 md:py-40 px-6 md:px-12 bg-[#0E0E0E]">
        <div className="max-w-6xl mx-auto">
          <Reveal>
            <p className="eyebrow">The Menu</p>
            <h2 className="serif text-4xl md:text-6xl mt-4 leading-tight max-w-2xl">
              Select your services <span className="serif-italic text-[#B8935A]">— craft your set.</span>
            </h2>
            <p className="mt-6 text-[#A1A1AA] max-w-xl leading-relaxed">
              Choose one or many. Every appointment is tailored to your chosen combination.
            </p>
          </Reveal>

          <div className="mt-16 space-y-16">
            {categories.map((cat) => (
              <Reveal key={cat.category}>
                <div className="flex items-baseline justify-between gap-6 mb-8">
                  <h3 className="serif text-2xl md:text-3xl text-[#F7F3EC]">{cat.category}</h3>
                  <div className="flex-1 h-px bg-[rgba(184,147,90,0.25)]" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {cat.items.map((name) => {
                    const active = selected.has(name);
                    return (
                      <button
                        key={name}
                        onClick={() => toggle(name)}
                        data-testid={`service-card-${name}`}
                        className={`text-left p-6 border transition-colors duration-200 group ${
                          active
                            ? "border-[#B8935A] bg-[#B8935A]/8"
                            : "border-[rgba(247,243,236,0.12)] hover:border-[#B8935A]"
                        }`}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <p className="serif text-xl text-[#F7F3EC] leading-snug">{name}</p>
                            <p className="text-xs tracking-[0.2em] uppercase mt-3 text-[#A1A1AA]">
                              {active ? "Selected" : "Tap to select"}
                            </p>
                          </div>
                          <div className={`w-6 h-6 border flex items-center justify-center shrink-0 transition-colors ${active ? "bg-[#B8935A] border-[#B8935A]" : "border-[#A1A1AA]"}`}>
                            {active && <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2 6L5 9L10 3" stroke="#0B0B0B" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </Reveal>
            ))}
          </div>

          {/* Selection summary */}
          <div className="mt-16 border border-[rgba(184,147,90,0.25)] p-6 md:p-8 bg-[#0B0B0B]" data-testid="selection-summary">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div className="flex-1">
                <p className="eyebrow">Your Selection</p>
                <p className="serif text-2xl md:text-3xl mt-3">
                  {selected.size === 0 ? "Nothing selected yet" : `${selected.size} service${selected.size > 1 ? "s" : ""} selected`}
                </p>
                {selected.size > 0 && (
                  <div className="mt-4 flex flex-wrap gap-2">
                    {Array.from(selected).map((s) => (
                      <span key={s} className="text-xs tracking-[0.15em] uppercase border border-[#B8935A] text-[#B8935A] px-3 py-1.5">
                        {s}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <Link to={bookingHref} data-testid="services-continue-btn" className="btn-primary whitespace-nowrap">
                Continue to Booking <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* LOCATION */}
      <section id="location" className="relative py-24 md:py-40 px-6 md:px-12">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-start">
          <Reveal>
            <p className="eyebrow">Find Us</p>
            <h2 className="serif text-4xl md:text-6xl mt-4 leading-tight">
              Visit <span className="serif-italic text-[#B8935A]">NOIR NAILS</span>
            </h2>
            <div className="gold-line my-8 max-w-[120px]" />
            <p className="text-[#A1A1AA] leading-relaxed text-base md:text-lg">{CONTACT.address}</p>
            <div className="mt-8 space-y-4">
              <div className="flex items-center gap-4"><MapPin size={16} className="text-[#B8935A]" /><span className="text-sm text-[#F7F3EC]">Whitefield · Near ITPL & Phoenix Marketcity</span></div>
              <div className="flex items-center gap-4"><Phone size={16} className="text-[#B8935A]" /><span className="text-sm text-[#F7F3EC]">+91 {CONTACT.phone}</span></div>
            </div>
            <div className="mt-10 flex flex-wrap gap-4">
              <a href={CONTACT.mapsUrl} target="_blank" rel="noreferrer" data-testid="location-directions" className="btn-primary">
                <Navigation size={14} /> Get Directions
              </a>
              <a href={`https://wa.me/${CONTACT.whatsapp}`} target="_blank" rel="noreferrer" data-testid="location-whatsapp" className="btn-outline">
                <MessageCircle size={14} /> WhatsApp
              </a>
            </div>
          </Reveal>
          <Reveal>
            <div className="border border-[rgba(184,147,90,0.25)]">
              <iframe
                title="NOIR NAILS location"
                src={CONTACT.mapsEmbed}
                className="w-full h-[420px] md:h-[500px]"
                style={{ border: 0, filter: "grayscale(0.6) contrast(1.05)" }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" className="relative py-24 md:py-32 px-6 md:px-12 bg-[#0E0E0E]">
        <div className="max-w-6xl mx-auto grid md:grid-cols-3 gap-12">
          <Reveal>
            <p className="eyebrow">Studio</p>
            <p className="serif text-2xl md:text-3xl mt-4">NOIR NAILS</p>
            <p className="mt-3 text-[#A1A1AA] text-sm">{CONTACT.hours}</p>
          </Reveal>
          <Reveal>
            <p className="eyebrow">Reach Us</p>
            <p className="mt-4 text-[#F7F3EC] text-base">+91 {CONTACT.phone}</p>
            <div className="mt-6 flex flex-col gap-3">
              <a href={`https://wa.me/${CONTACT.whatsapp}`} target="_blank" rel="noreferrer" data-testid="contact-whatsapp" className="btn-outline">WhatsApp Us</a>
              <a href={`tel:+91${CONTACT.phone}`} data-testid="contact-call" className="btn-outline">Call Us</a>
              <a href={CONTACT.mapsUrl} target="_blank" rel="noreferrer" data-testid="contact-directions" className="btn-outline">Get Directions</a>
            </div>
          </Reveal>
          <Reveal>
            <p className="eyebrow">Follow</p>
            <p className="mt-4 text-[#A1A1AA] text-sm leading-relaxed">Discover our latest sets, nail inspiration and new designs.</p>
            <a href={CONTACT.instagram} target="_blank" rel="noreferrer" data-testid="contact-instagram" className="btn-gold mt-6 inline-flex">
              <Instagram size={14} /> @noirnails_bangalore
            </a>
          </Reveal>
        </div>
      </section>

      {/* INSTAGRAM */}
      <section id="instagram" className="relative py-24 md:py-32 px-6 md:px-12">
        <div className="max-w-6xl mx-auto">
          <Reveal>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
              <div>
                <p className="eyebrow">Follow NOIR NAILS</p>
                <h2 className="serif text-4xl md:text-6xl mt-4 leading-tight">
                  From our <span className="serif-italic text-[#B8935A]">studio</span> to your feed
                </h2>
              </div>
              <a href={CONTACT.instagram} target="_blank" rel="noreferrer" data-testid="instagram-follow" className="btn-outline whitespace-nowrap">
                <Instagram size={14} /> Follow @noirnails_bangalore
              </a>
            </div>
          </Reveal>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 md:gap-4">
            {IG_IMGS.map((src, i) => (
              <a key={i} href={CONTACT.instagram} target="_blank" rel="noreferrer" className={`group relative overflow-hidden aspect-square block ${i === 4 ? "col-span-2 md:col-span-1" : ""}`} data-testid={`instagram-tile-${i}`}>
                <img src={src} alt={`NOIR NAILS design ${i + 1}`} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" loading="lazy" />
                <div className="absolute inset-0 bg-[#0B0B0B]/0 group-hover:bg-[#0B0B0B]/40 transition-colors flex items-center justify-center">
                  <Instagram size={22} className="text-[#F7F3EC] opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      <Footer />
      <StickyMobileCTA />
    </div>
  );
}
